import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Package, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { purchaseApi, type OrderStatus } from '@/lib/api';
import { reachGoal, GOAL_PURCHASE_CONFIRM, GOAL_PURCHASE_DELIVERED, GOAL_PURCHASE_ERROR } from '@/lib/metrika';

interface Props {
  transactionId: number | null;
  onClose: () => void;
  /** Вызывается один раз при первом переходе статуса в paid/completed.
   *  Используется родителем для закрытия popup-окна оплаты. */
  onPaymentConfirmed?: () => void;
  /** URL и данные формы для повторного открытия страницы оплаты. */
  paymentUrl?: string;
  paymentFormData?: Record<string, string>;
}

const TERMINAL_ORDER = new Set(['DONE', 'ERROR', 'RETURNED']);
const TERMINAL_XPANDA = new Set(['DONE', 'ERROR']);
const POLL_MS_PENDING = 4_000;  // пока ждём оплаты — быстрый поллинг
const POLL_MS_ACTIVE  = 10_000; // когда paid/processing — стандартный

/** Xpanda возвращает статусы в разном кейсе ('Done', 'In progress', etc) — нормализуем. */
const norm = (s?: string | null) => (s || '').trim().toUpperCase();

function isTerminal(d: OrderStatus): boolean {
  const o = norm(d.order_status);
  const x = norm(d.xpanda_status);
  if (o && TERMINAL_ORDER.has(o)) return true;
  if (x && TERMINAL_XPANDA.has(x)) return true;
  if (d.status === 'failed' || d.status === 'cancelled' || d.status === 'refunded') return true;
  return false;
}

function isDelivered(d: OrderStatus): boolean {
  // active_order пропадает после завершения, но xpanda_status=DONE остаётся
  return norm(d.order_status) === 'DONE' || norm(d.xpanda_status) === 'DONE';
}

function isErrored(d: OrderStatus): boolean {
  if (d.status === 'failed' || d.status === 'cancelled') return true;
  const o = norm(d.order_status);
  const x = norm(d.xpanda_status);
  if (o === 'ERROR' || o === 'RETURNED') return true;
  if (x === 'ERROR') return true;
  return false;
}

/** Дедуп цели по transaction_id через sessionStorage — переживает переоткрытие модала. */
function fireGoalOnce(goal: string, txId: number, params: Record<string, unknown>) {
  const key = `metrika_${goal}_${txId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // если storage недоступен — пусть стреляет, лучше дубль чем ноль
  }
  reachGoal(goal, params);
}

const LOCAL_LABELS: Record<string, string> = {
  pending:   'Ожидает оплаты',
  paid:      'Оплачено',
  completed: 'Завершено',
  failed:    'Ошибка',
  cancelled: 'Отменено',
  refunded:  'Возвращено на баланс',
};

const LOCAL_STYLE: Record<string, string> = {
  pending:   'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  paid:      'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed:    'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  refunded:  'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const ORDER_STYLE: Record<string, string> = {
  DONE:              'bg-green-500/20 text-green-400 border-green-500/30',
  ERROR:             'bg-red-500/20 text-red-400 border-red-500/30',
  RETURNED:          'bg-orange-500/20 text-orange-400 border-orange-500/30',
  TRADE:             'bg-blue-500/20 text-blue-400 border-blue-500/30',
  WAITING_FOR_TRADE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  BOUGHT:            'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

export function OrderStatusModal({ transactionId, onClose, onPaymentConfirmed, paymentUrl, paymentFormData }: Props) {
  const [data, setData] = useState<OrderStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [terminal, setTerminal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Флаг: onPaymentConfirmed уже вызван для текущей транзакции — не дублируем
  const confirmedFiredRef = useRef(false);

  /** Повторно открыть окно оплаты — используем те же данные формы что при первом открытии. */
  const reopenPayment = useCallback(() => {
    if (!paymentUrl || !paymentFormData) return;
    try {
      const popup = window.open('', 'xpanda-payment', 'width=560,height=700,scrollbars=yes,resizable=yes');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentUrl;
      form.target = popup ? 'xpanda-payment' : '_blank';
      form.enctype = 'application/x-www-form-urlencoded';
      form.style.display = 'none';
      Object.entries(paymentFormData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (err) {
      console.error('[PAYMENT] reopen error:', err);
    }
  }, [paymentUrl, paymentFormData]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const result = await purchaseApi.getStatus(transactionId);
      setData(result);
      const txId = result.transaction_id;
      console.log('[OrderStatusModal] poll', {
        tx: txId,
        status: result.status,
        xpanda_status: result.xpanda_status,
        order_status: result.order_status,
        deal_id: result.xpanda_deal_id,
      });
      // Фиксируем подтверждение оплаты при первом paid/completed
      if (result.status === 'paid' || result.status === 'completed') {
        fireGoalOnce(GOAL_PURCHASE_CONFIRM, txId, {
          transaction_id: txId,
          item: result.item_name,
          amount: result.amount,
        });
        // Закрываем popup-окно оплаты со стороны основной страницы
        if (!confirmedFiredRef.current) {
          confirmedFiredRef.current = true;
          onPaymentConfirmed?.();
        }
      }
      if (isTerminal(result)) {
        stopPolling();
        setTerminal(true);
        // Фиксируем доставку при DONE (xpanda_status или order_status)
        if (isDelivered(result)) {
          fireGoalOnce(GOAL_PURCHASE_DELIVERED, txId, {
            transaction_id: txId,
            item: result.item_name,
            amount: result.amount,
          });
        }
        // Фиксируем ошибку при ERROR/RETURNED/failed/cancelled
        if (isErrored(result)) {
          fireGoalOnce(GOAL_PURCHASE_ERROR, txId, {
            transaction_id: txId,
            item: result.item_name,
            reason: result.xpanda_status || result.order_status || result.status,
            error_code: result.error_code,
          });
        }
      }
    } catch (e: any) {
      setFetchError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [transactionId, stopPolling]);

  // Сброс + первый немедленный запрос при открытии модала
  useEffect(() => {
    if (!transactionId) {
      setData(null);
      setFetchError(null);
      setTerminal(false);
      confirmedFiredRef.current = false;
      stopPolling();
      return;
    }
    confirmedFiredRef.current = false;
    fetchStatus();
  }, [transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Динамический интервал поллинга: 4с пока status=pending, 10с после оплаты
  useEffect(() => {
    if (!transactionId || terminal) return stopPolling;
    const ms = (!data || data.status === 'pending') ? POLL_MS_PENDING : POLL_MS_ACTIVE;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchStatus, ms);
    return stopPolling;
  }, [transactionId, data?.status, terminal, fetchStatus, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // postMessage от PaymentCallbackPage → немедленный опрос после успешной оплаты
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'xpanda_payment_done' && transactionId && !terminal) {
        fetchStatus();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [transactionId, terminal, fetchStatus]);

  // visibilitychange fallback: пользователь вернулся на вкладку → опросить статус
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && transactionId && !terminal) {
        fetchStatus();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [transactionId, terminal, fetchStatus]);

  return (
    <Dialog open={transactionId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Статус заказа</DialogTitle>
        </DialogHeader>

        {loading && !data && (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-8 h-8 text-[#ff6b00] animate-spin" />
          </div>
        )}

        {fetchError && !data && (
          <p className="text-red-400 text-sm py-4 text-center">{fetchError}</p>
        )}

        {data && (
          <div className="space-y-4">
            {/* Товар */}
            <div className="flex items-center gap-4 p-3 bg-[#0d1117] rounded-lg border border-[#30363d]">
              {data.item_image ? (
                <img
                  src={data.item_image}
                  alt={data.item_name || ''}
                  className="w-16 h-16 object-contain rounded"
                />
              ) : (
                <div className="w-16 h-16 bg-[#1a2332] rounded flex items-center justify-center">
                  <Package className="w-8 h-8 text-[#8b949e]" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">{data.item_name || 'Предмет'}</p>
                <p className="text-[#8b949e] text-sm">
                  {data.amount?.toLocaleString('ru-RU')} {data.currency}
                </p>
              </div>
            </div>

            {/* ID заказа */}
            <div className="flex justify-between text-sm">
              <span className="text-[#8b949e]">ID заказа</span>
              <span className="text-white font-mono">
                {data.xpanda_custom_id || `#${data.transaction_id}`}
              </span>
            </div>

            {/* Статус оплаты */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-[#8b949e]">Оплата</span>
              <Badge className={LOCAL_STYLE[data.status] || 'bg-gray-500/20 text-gray-400'}>
                {LOCAL_LABELS[data.status] || data.status}
              </Badge>
            </div>

            {/* refunded — деньги вернулись на баланс (Xpanda не доставил) */}
            {data.status === 'refunded' ? (
              <div className="p-3 bg-teal-900/20 border border-teal-500/30 rounded-lg text-sm text-teal-300">
                Скин не удалось купить — {data.amount?.toLocaleString('ru-RU')} {data.currency} возвращены на ваш баланс. Можно купить другой предмет.
              </div>
            ) : data.xpanda_deal_id ? (
              <>
                {data.xpanda_status_label && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#8b949e]">Статус сделки</span>
                    <Badge className={ORDER_STYLE[data.xpanda_status || ''] || 'bg-yellow-500/20 text-yellow-400'}>
                      {data.xpanda_status_label}
                    </Badge>
                  </div>
                )}
                {data.order_status_label && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#8b949e]">Доставка</span>
                    <Badge className={ORDER_STYLE[data.order_status || ''] || 'bg-yellow-500/20 text-yellow-400'}>
                      {data.order_status_label}
                    </Badge>
                  </div>
                )}
                {data.steam_trade_offer_id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8b949e]">Trade Offer ID</span>
                    <span className="text-white font-mono text-xs">{data.steam_trade_offer_id}</span>
                  </div>
                )}
                {data.error_code && (
                  <div className="p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg text-sm text-orange-300">
                    Сделка не завершилась: {data.error_code}. Если деньги были списаны — они вернутся на баланс автоматически.
                  </div>
                )}
              </>
            ) : (data.status === 'completed' || data.status === 'paid') ? (
              <p className="text-[#8b949e] text-sm text-center py-1">
                Создаём сделку в Xpanda...
              </p>
            ) : null}

            {data.error && !data.error_code && (
              <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-sm text-red-400">
                {data.error}
              </div>
            )}

            {/* Кнопка повторного открытия страницы оплаты */}
            {data.status === 'pending' && !terminal && paymentUrl && (
              <Button
                size="sm"
                onClick={reopenPayment}
                className="w-full bg-[#1a2332] hover:bg-[#253040] text-white border border-[#30363d]"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Открыть страницу оплаты
              </Button>
            )}

            {/* VPN-подсказка — показываем пока ждём оплаты */}
            {data.status === 'pending' && !terminal && (
              <div className="flex items-start gap-2 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg text-sm text-amber-300">
                <span className="text-base leading-none mt-0.5">🔒</span>
                <span>Если страница оплаты не загружается, выключите VPN.</span>
              </div>
            )}

            {/* Кнопки */}
            <div className="flex flex-col gap-2 pt-2">
              {/* «Я уже оплатил» — только пока ждём оплаты */}
              {data?.status === 'pending' && !terminal && (
                <Button
                  size="sm"
                  onClick={fetchStatus}
                  disabled={loading}
                  className="w-full bg-[#ff6b00] hover:bg-[#ff8533] text-white"
                >
                  {loading
                    ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Проверяем...</>
                    : 'Я уже оплатил →'
                  }
                </Button>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchStatus}
                  disabled={loading || terminal}
                  className="flex-1 border-[#30363d] text-[#8b949e] hover:text-white hover:bg-[#1a2332] disabled:opacity-40"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {terminal ? 'Финальный статус' : 'Обновить'}
                </Button>
                <Button
                  size="sm"
                  onClick={onClose}
                  className="flex-1 bg-[#ff6b00] hover:bg-[#ff8533] text-white"
                >
                  Закрыть
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
